import {
  devicesMetrics,
  isModelVirtualWeatherStation,
  isVirtualWeatherStation
} from '@weenat/client/dist/core/devices'
import isIrrigationDevice from '@weenat/client/dist/core/devices/isIrrigationDevice'
import {
  getAvailableTimeStepsForCustomPeriods,
  getNextPeriodOf,
  getPeriodByPeriodId,
  getPreviousPeriodOf,
  HistoryPeriod,
  PredefinedPeriodIds
} from '@weenat/client/dist/core/history'
import { preferredMetricsOrder } from '@weenat/client/dist/core/metrics'
import { Brand, getLastUpdateThresholds } from '@weenat/client/dist/enums/Devices'
import { isMetricVisibleOnWeatherMapSummary } from '@weenat/client/dist/enums/MetricIds'
import { TimeSteps } from '@weenat/client/dist/enums/TimeSteps'
import { Device } from '@weenat/client/dist/resources/devices'
import { useIntl } from '@weenat/wintl'
import {
  useDeviceDashboardSections,
  useDeviceDashboardSharedPeriod,
  useToggleFeature
} from 'app/state'
import { isArray, isNil } from 'lodash-es'
import { FC, useCallback, useMemo } from 'react'
import { styled } from 'styled-components'
import CollapsibleSection from '../kit/CollapsibleSection'
import NoEmissionEmptyState from '../kit/empty-state/NoEmissionEmptyState'
import { SelectFieldPrimitive } from '../kit/fields/SelectField'
import Grid from '../kit/Grid'
import Icons from '../kit/Icons'
import LastUpdateBadge from '../kit/LastUpdateBadge'
import PeriodSelector from '../kit/PeriodSelector'
import Text from '../kit/Text'
import { UpdateInformationTiming } from '../kit/UpdateInformation'
import LatestValueCard from '../kit/weedget/LatestValueCard'
import MetricWeedget from '../kit/weedget/MetricWeedget'
import DeviceMetricCard from './DeviceMetricCard'
import IrrigationDeviceDashboardEmptyState from './IrrigationDeviceDashboardEmptyState'

interface DeviceDashboardProps {
  device: Device
}

/** @deprecated remove with sdh */
const LegacyDeviceDashboard: FC<DeviceDashboardProps> = ({ device }) => {
  const { t } = useIntl()
  const [{ marker_redesign, smv }] = useToggleFeature()

  const visibleMetrics = device.availableMeasures.filter(
    (metric) => isMetricVisibleOnWeatherMapSummary(metric) || metric === 'LW_D'
  )

  const sortedVisibleMetrics = useMemo(
    () =>
      visibleMetrics.sort(
        (a, b) => preferredMetricsOrder.indexOf(a) - preferredMetricsOrder.indexOf(b)
      ),
    [visibleMetrics]
  )

  const metricsToDisplay = useMemo(() => {
    return device.availableMeasures.filter((metric) => devicesMetrics.includes(metric))
  }, [device.availableMeasures])

  const lastUpdateThresholds = getLastUpdateThresholds(
    device.brand,
    undefined,
    smv ? isVirtualWeatherStation(device) : false
  )

  const showIrrigationEmptyState = isIrrigationDevice(device) && device?.brand !== Brand.corhize

  return isNil(device.lastMeasuredAt) ? (
    <Flex
      $height={'100%'}
      $flexDirection='column'
      $alignItems='center'
      $p='lg'
      $gap='xl'
      $mx='auto'
      $maxWidth={1280}
    >
      <NoEmissionEmptyState
        IconName={
          isModelVirtualWeatherStation(device.model)
            ? 'SingleVirtualSensorFilled'
            : 'SingleSensorFilled'
        }
        title={t('weedget.not_emitted_yet.wait_message')}
        description={t('weedget.not_emitted_yet.waiting_for_sensor')}
        metrics={metricsToDisplay}
      />
    </Flex>
  ) : (
    <Flex $flexDirection='column' $p='lg' $gap='xl' $mx='auto' $maxWidth={1280}>
      {showIrrigationEmptyState ? (
        <IrrigationDeviceDashboardEmptyState device={device} />
      ) : (
        <>
          <Flex $flexDirection='column' $gap='md'>
            <Flex $gap='sm'>
              <Text $fontWeight='bold'>
                {t(smv ? 'map.universes.aggregate.last-measured' : 'periods.latest', {
                  capitalize: true
                })}
              </Text>
              <Flex $alignItems='center' $gap='xs'>
                {marker_redesign ? (
                  <LastUpdateBadge
                    timestamp={parseInt(device.lastMeasuredAt, 10)}
                    timezone={device.timezone}
                    warningThreshold={lastUpdateThresholds.warningThreshold}
                    dangerThreshold={lastUpdateThresholds.dangerThreshold}
                  />
                ) : (
                  <UpdateInformationTiming timestamp={parseInt(device.lastMeasuredAt, 10)} />
                )}
              </Flex>
            </Flex>
            <Grid $templateColumns='1fr 1fr 1fr' $gap='md'>
              {sortedVisibleMetrics.map((metric) => (
                <LatestValueCard key={metric} metric={metric} />
              ))}
            </Grid>
          </Flex>
          <Flex $flexDirection='column' $gap='md'>
            <Text $fontWeight='bold'>{t('periods.recently', { capitalize: true })}</Text>
            <Flex $flexDirection='column' $gap='md'>
              {sortedVisibleMetrics.map((metric) => (
                <Flex key={metric} $height={280}>
                  <MetricWeedget metric={metric} />
                </Flex>
              ))}
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  )
}

const SharedPeriodBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${(p) => p.theme.spacings.md}px;
  flex-wrap: wrap;
`

const NewDeviceDashboard: FC<DeviceDashboardProps> = ({ device }) => {
  const { t } = useIntl()
  const [{ marker_redesign, smv }] = useToggleFeature()
  const [sections, setSections] = useDeviceDashboardSections()
  const [storedSharedPeriod, setStoredSharedPeriod] = useDeviceDashboardSharedPeriod()

  const defaultSharedPeriod = useMemo(() => {
    const period = getPeriodByPeriodId({
      periodId: 'last_7_days',
      timezone: device.timezone
    })
    return {
      since: period.since,
      before: period.before,
      selectedPeriodId: 'last_7_days',
      selectedTimeStep: TimeSteps.perHours
    }
  }, [device.timezone])

  const sharedPeriodState = storedSharedPeriod ?? defaultSharedPeriod

  const recentlySectionPeriod: HistoryPeriod = useMemo(
    () => ({
      since: sharedPeriodState.since,
      before: sharedPeriodState.before
    }),
    [sharedPeriodState.since, sharedPeriodState.before]
  )

  const visibleMetrics = device.availableMeasures.filter(
    (metric) => isMetricVisibleOnWeatherMapSummary(metric) || metric === 'LW_D'
  )

  const sortedVisibleMetrics = useMemo(
    () =>
      visibleMetrics.sort(
        (a, b) => preferredMetricsOrder.indexOf(a) - preferredMetricsOrder.indexOf(b)
      ),
    [visibleMetrics]
  )

  const timeStepsOptions = useMemo(
    () =>
      getAvailableTimeStepsForCustomPeriods({
        metrics: device.availableMeasures,
        since: sharedPeriodState.since,
        before: sharedPeriodState.before,
        timezone: device.timezone
      })
        .filter((timeStep) => timeStep !== TimeSteps.maximum)
        .map((timeStep) => ({
          value: timeStep,
          label: t(`timeSteps.${timeStep}`, { capitalize: true })
        })),
    [
      device.availableMeasures,
      device.timezone,
      sharedPeriodState.before,
      sharedPeriodState.since,
      t
    ]
  )

  const lastUpdateThresholds = getLastUpdateThresholds(
    device.brand,
    undefined,
    smv ? isVirtualWeatherStation(device) : false
  )

  const showIrrigationEmptyState = isIrrigationDevice(device) && device?.brand !== Brand.corhize

  const latestBadge = marker_redesign ? (
    <LastUpdateBadge
      timestamp={parseInt(device.lastMeasuredAt, 10)}
      timezone={device.timezone}
      warningThreshold={lastUpdateThresholds.warningThreshold}
      dangerThreshold={lastUpdateThresholds.dangerThreshold}
    />
  ) : (
    <UpdateInformationTiming timestamp={parseInt(device.lastMeasuredAt, 10)} />
  )

  const onNext = useCallback(() => {
    if (!isNil(storedSharedPeriod)) {
      getNextPeriodOf({
        period: {
          since: storedSharedPeriod?.since,
          before: storedSharedPeriod?.before
        },
        periodId: storedSharedPeriod?.selectedPeriodId,
        timeStep: storedSharedPeriod?.selectedTimeStep,
        timezone: device.timezone
      })
    }
  }, [storedSharedPeriod, device.timezone])

  const onPrevious = useCallback(() => {
    if (!isNil(storedSharedPeriod)) {
      getPreviousPeriodOf({
        period: {
          since: storedSharedPeriod?.since,
          before: storedSharedPeriod?.before
        },
        periodId: storedSharedPeriod?.selectedPeriodId,
        timeStep: storedSharedPeriod?.selectedTimeStep,
        timezone: device.timezone
      })
    }
  }, [storedSharedPeriod, device.timezone])

  const onPeriodChange = useCallback(
    (period: { since: number; before: number }, periodId: PredefinedPeriodIds | undefined) =>
      setStoredSharedPeriod((current) => {
        if (isNil(current)) {
          return {
            ...defaultSharedPeriod,
            since: period.since,
            before: period.before,
            selectedPeriodId: periodId ?? 'custom'
          }
        }
        return {
          ...current,
          since: period.since,
          before: period.before,
          selectedPeriodId: periodId ?? 'custom'
        }
      }),
    [defaultSharedPeriod, setStoredSharedPeriod]
  )

  return isNil(device.lastMeasuredAt) ? (
    <Flex
      $height={'100%'}
      $flexDirection='column'
      $alignItems='center'
      $p='lg'
      $gap='xl'
      $mx='auto'
      $maxWidth={1280}
    >
      <NoEmissionEmptyState
        IconName={
          isModelVirtualWeatherStation(device.model)
            ? 'SingleVirtualSensorFilled'
            : 'SingleSensorFilled'
        }
        title={t('weedget.not_emitted_yet.wait_message')}
        description={t('weedget.not_emitted_yet.waiting_for_sensor')}
        metrics={device.availableMeasures.filter((metric) => devicesMetrics.includes(metric))}
      />
    </Flex>
  ) : (
    <Flex $flexDirection='column' $p='lg' $gap='xl' $mx='auto' $maxWidth={1280}>
      {showIrrigationEmptyState ? (
        <IrrigationDeviceDashboardEmptyState device={device} />
      ) : (
        <>
          <CollapsibleSection
            title={t(smv ? 'map.universes.aggregate.last-measured' : 'periods.latest', {
              capitalize: true
            })}
            badge={latestBadge}
            isOpen={sections.latest}
            onToggle={() => setSections((s) => ({ ...s, latest: !s.latest }))}
          >
            <Grid $templateColumns='1fr 1fr 1fr' $gap='md'>
              {sortedVisibleMetrics.map((metric) => (
                <LatestValueCard key={metric} metric={metric} />
              ))}
            </Grid>
          </CollapsibleSection>
          <CollapsibleSection
            title={t('periods.recently', { capitalize: true })}
            isOpen={sections.recently}
            onToggle={() => setSections((s) => ({ ...s, recently: !s.recently }))}
          >
            <Flex $flexDirection='column' $gap='md'>
              <SharedPeriodBar>
                <PeriodSelector
                  since={sharedPeriodState.since}
                  before={sharedPeriodState.before}
                  currentPeriodId={sharedPeriodState.selectedPeriodId}
                  timeStep={sharedPeriodState.selectedTimeStep}
                  timezone={device.timezone}
                  onChange={onPeriodChange}
                  onNext={onNext}
                  onPrevious={onPrevious}
                />
                <SelectFieldPrimitive
                  name='shared_dashboard_frequency'
                  leftAdornment={<Icons.Frequency $size='lg' />}
                  value={sharedPeriodState.selectedTimeStep}
                  options={timeStepsOptions}
                  onChange={(value) => {
                    if (isNil(value) || isArray(value)) return
                    setStoredSharedPeriod((current) => {
                      if (isNil(current)) {
                        return { ...defaultSharedPeriod, selectedTimeStep: value }
                      }
                      return {
                        ...current,
                        selectedTimeStep: value
                      }
                    })
                  }}
                  label={undefined}
                  hideErrors
                  isClearable={false}
                  defaultOnFirstOption
                />
              </SharedPeriodBar>
              {sortedVisibleMetrics.map((metric) => (
                <DeviceMetricCard
                  key={metric}
                  metric={metric}
                  period={recentlySectionPeriod}
                  timeStep={sharedPeriodState.selectedTimeStep}
                  deviceId={device.id}
                  isVWS={isModelVirtualWeatherStation(device.model)}
                />
              ))}
            </Flex>
          </CollapsibleSection>
        </>
      )}
    </Flex>
  )
}

const DeviceDashboard: FC<DeviceDashboardProps> = ({ device }) => {
  const [{ sdh }] = useToggleFeature()
  return sdh ? <NewDeviceDashboard device={device} /> : <LegacyDeviceDashboard device={device} />
}

export default DeviceDashboard
